import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { mobilePaymentService } from '../services/mobilePayment';

const CCAvPaymentCard = ({ 
  flat, 
  amount,
  onClose,
  onPaymentSuccess,
  onPaymentError 
}) => {
  const { user, getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const formRef = useRef(null);
  const iframeRef = useRef(null);

  // Form state for billing information
  const [billingForm, setBillingForm] = useState({
    billingName: user?.attributes?.given_name + ' ' + user?.attributes?.family_name || '',
    billingEmail: user?.attributes?.email || '',
    billingPhone: user?.attributes?.phone_number || '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    paymentMethod: 'CCAvenue'
  });

  const [paymentData, setPaymentData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're running on mobile app
  useEffect(() => {
    setIsMobile(Capacitor.isNativePlatform());
  }, []);

  // Monitor for mobile payment completion
  useEffect(() => {
    if (isMobile && paymentProcessing) {
      // Simple polling to detect payment completion and clear processing state
      const checkInterval = setInterval(() => {
        const storedResult = localStorage.getItem('mobilePaymentResult');
        const pendingPayment = localStorage.getItem('pendingPayment');
        
        // If no pending payment, payment is complete
        if (!pendingPayment) {
          setPaymentProcessing(false);
          clearInterval(checkInterval);
        }
        
        // If we have a stored result, payment is definitely complete
        if (storedResult) {
          setPaymentProcessing(false);
          clearInterval(checkInterval);
        }
      }, 1000);
      
      // Clean up after 3 minutes
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        setPaymentProcessing(false);
      }, 3 * 60 * 1000);
      
      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, [isMobile, paymentProcessing]);

  const handleInputChange = (field, value) => {
    setBillingForm(prev => ({ ...prev, [field]: value }));
  };

  const handleInitiatePayment = () => {
    setShowPaymentForm(true);
    setError('');
  };

  const handleCancelPayment = () => {
    setShowPaymentForm(false);
    setPaymentProcessing(false);
    setError('');
    setPaymentData(null);
  };

  // Payment callback is now handled by React route /payment-callback
  // No need for postMessage handling since we're using direct navigation

  const handlePaymentCallback = async (callbackData, status) => {
    if (!paymentData) {
      console.error('No payment data available for callback processing');
      return;
    }

    try {
      setLoading(true);
      
      // Update payment status using paymentsAPI.update similar to Razorpay
      await paymentsAPI.payWithCCAvenue({
        paymentId: paymentData.paymentId,
        flatId: flat.flatId,
        status: status,
        amount: amount || flat.outstandingBalance,
        description: status === 'COMPLETED' ? 'Payment completed via CCAvenue' : 'Payment failed via CCAvenue',
        ccavenueTransactionId: callbackData.transactionId,
        ccavenueOrderId: callbackData.paymentId,
        ccavenueStatus: callbackData.status,
        buildingId: flat.buildingId || flat.buildingID,
        paymentMethod: 'CCAvenue'
      });
      
      if (status === 'COMPLETED') {
        // Show success and refresh page
        if (onPaymentSuccess) {
          onPaymentSuccess({
            paymentId: paymentData.paymentId,
            status: 'COMPLETED',
            message: 'CCAvenue payment completed successfully!'
          });
        }
        
        // Reset form and refresh page after delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        
      } else {
        // Show error
        const errorMsg = callbackData.message || 'Payment failed. Please try again.';
        setError(errorMsg);
        if (onPaymentError) {
          onPaymentError(errorMsg);
        }
        
        // Reset form
        setShowPaymentForm(false);
        setPaymentProcessing(false);
      }
      
    } catch (error) {
      console.error('Error updating payment status:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update payment status';
      setError(errorMsg);
      if (onPaymentError) {
        onPaymentError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPayment = async () => {
    setError('');
    setLoading(true);
    setPaymentProcessing(true);

    try {
      // Store authentication token for callback usage
      if (getAuthToken) {
        try {
          const token = await getAuthToken();
          sessionStorage.setItem('ccavenue_auth_token', token);
        } catch (tokenError) {
          console.warn('Could not get auth token:', tokenError);
        }
      }

      // Validate required fields
      if (!billingForm.billingName || !billingForm.billingEmail || !billingForm.billingPhone) {
        setError('Please fill in all required billing information');
        setLoading(false);
        setPaymentProcessing(false);
        return;
      }

      if (!(amount || flat.outstandingBalance) || !flat.flatId || !flat.buildingId) {
        setError('Missing payment information');
        setLoading(false);
        setPaymentProcessing(false);
        return;
      }

      // Generate unique payment ID
      const paymentId = `PAY_${Date.now()}_${flat.flatId}`;

      // Prepare payment request
      const paymentRequest = {
        paymentId,
        amount: parseFloat(amount || flat.outstandingBalance),
        flatId: flat.flatId,
        buildingId: flat.buildingId || flat.buildingID,
        userId: user?.username || user?.attributes?.sub,
        paymentType: 'OutstandingBalance',
        description: `Outstanding balance payment for Flat ${flat.flatNumber || flat.flatId}`,
        billingName: billingForm.billingName,
        billingEmail: billingForm.billingEmail,
        billingPhone: billingForm.billingPhone,
        billingAddress: billingForm.billingAddress || 'Address'
      };

      // Store payment data for callback processing
      setPaymentData(paymentRequest);

      // Initiate CCAvenue payment
      const response = await paymentsAPI.initiateCCAvPayment(paymentRequest);
      
      if (response.data && (response.data.enc_val || response.data.encRequest) && (response.data.access_code || response.data.accessCode)) {
        // Check if we're on mobile app
        if (isMobile) {
          // For mobile app, use WebView approach as per CCAvenue mobile integration guide
          try {
            console.log('Initiating mobile CCAvenue payment with WebView...');
            
            // Combine payment request data with CCAvenue response data for mobile
            const mobilePaymentData = {
              ...response.data, // CCAvenue encrypted data and access code
              ...paymentRequest, // Original payment request with paymentId, flatId, etc.
              ccavenueOrderId: paymentRequest.paymentId || paymentId, // Ensure orderId is available
              orderId: paymentRequest.paymentId || paymentId // Backup orderId field
            };
            
           
            // Use mobile payment service for WebView integration
            await mobilePaymentService.initiateMobilePayment(mobilePaymentData);
            
            // Payment is now handled in WebView - will return to PaymentCallback page
            console.log('Mobile payment WebView opened successfully');
            
          } catch (mobileError) {
            console.error('Mobile payment WebView error:', mobileError);
            setError('Failed to open mobile payment gateway. Please try again.');
            setPaymentProcessing(false);
          }
        } else {
          // Web browser - create and submit form directly in the same window
          const encRequest = response.data.enc_val || response.data.encRequest;
          const accessCode = response.data.access_code || response.data.accessCode;
          //const ccavenueUrl = response.data.ccavenueUrl || 'https://test.ccavenue.com/transaction/transaction.do?command=initiateTransaction';
          const ccavenueUrl = response.data.ccavenueUrl || 'https://secure.ccavenue.com/transaction/transaction.do?command=initiateTransaction';
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = ccavenueUrl;
          form.target = '_self'; // Navigate in the same window
          
          const encRequestInput = document.createElement('input');
          encRequestInput.type = 'hidden';
          encRequestInput.name = 'encRequest';
          encRequestInput.value = encRequest;
          
          const accessCodeInput = document.createElement('input');
          accessCodeInput.type = 'hidden';
          accessCodeInput.name = 'access_code';
          accessCodeInput.value = accessCode;
          
          form.appendChild(encRequestInput);
          form.appendChild(accessCodeInput);
          
          // Append to body and submit
          document.body.appendChild(form);
          form.submit();
        }
        
      } else {
        setError('Failed to initiate payment. Please try again.');
        setPaymentProcessing(false);
      }
    } catch (err) {
      console.error('CCAvenue payment error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Payment initiation failed';
      setError(errorMessage);
      setPaymentProcessing(false);
      if (onPaymentError) {
        onPaymentError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (paymentProcessing) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Redirecting to CCAvenue...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You are being redirected to CCAvenue's secure payment gateway.
              Please complete your payment there.
            </Typography>
            <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
              <strong>Note:</strong> After payment completion, you will be redirected back 
              to this page with the updated status.
            </Alert>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!showPaymentForm) {
    return (
      <Card sx={{ mb: 2, border: '2px solid #1976d2', boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" color="primary">
                CCAvenue Payment
              </Typography>
            </Box>
            {onClose && (
              <Button 
                onClick={onClose} 
                size="small" 
                sx={{ minWidth: 'auto', color: 'text.secondary' }}
              >
                ✕
              </Button>
            )}
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Pay <strong>₹{amount || flat.outstandingBalance}</strong> securely using CCAvenue.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            CCAvenue supports Credit/Debit Cards, Net Banking, UPI, and other payment methods.
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={
                  (e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) {
                      setTermsDialogOpen(true);
                      handleInitiatePayment();
                    }
                  }
                }
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                I agree to the{' '}
                <Typography
                  component="span"
                  onClick={() => {
                    setTermsDialogOpen(true);
                    //if (termsAccepted) {
                      //handleInitiatePayment();
                    //}
                  }}
                  sx={{ 
                    color: 'primary.main', 
                    cursor: 'pointer',
                    fontWeight: 'medium',
                    textDecoration: 'underline',
                    ml: 0.5,
                    mr: 0.5,
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  Terms and Conditions
                </Typography>
                for payment processing
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontStyle: 'italic',
                    ml: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  (click to view)
                </Typography>
              </Typography>
            }
            sx={{ 
              mb: 2, 
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                lineHeight: 1.4
              }
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="success.main">
              🔒 Secure Payment Gateway
            </Typography>
          </Box>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          {onClose && (
            <Button 
              onClick={onClose} 
              color="secondary"
              startIcon={<Typography>←</Typography>}
            >
              Back to Payment Methods
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
        </CardActions>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h6">
            CCAvenue Payment Details
          </Typography>
        </Box>

        {/* Payment Summary */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Payment Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography><strong>Amount:</strong> ₹{amount || flat.outstandingBalance}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Flat:</strong> {flat.flatNumber || flat.flatId}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Type:</strong> Outstanding Balance</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Building:</strong> {flat.buildingName}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Billing Information Form */}
        <Typography variant="h6" gutterBottom>
          Billing Information
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name*"
              value={billingForm.billingName}
              onChange={(e) => handleInputChange('billingName', e.target.value)}
              disabled={loading}
              required
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email*"
              type="email"
              value={billingForm.billingEmail}
              onChange={(e) => handleInputChange('billingEmail', e.target.value)}
              disabled={loading}
              required
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number*"
              value={billingForm.billingPhone}
              onChange={(e) => handleInputChange('billingPhone', e.target.value)}
              disabled={loading}
              required
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={billingForm.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="CCAvenue">CCAvenue - Cards, Net Banking, UPI</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={billingForm.billingAddress}
              onChange={(e) => handleInputChange('billingAddress', e.target.value)}
              disabled={loading}
              placeholder="Enter your billing address"
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              value={billingForm.billingCity}
              onChange={(e) => handleInputChange('billingCity', e.target.value)}
              disabled={loading}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="State"
              value={billingForm.billingState}
              onChange={(e) => handleInputChange('billingState', e.target.value)}
              disabled={loading}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="PIN Code"
              value={billingForm.billingZip}
              onChange={(e) => handleInputChange('billingZip', e.target.value)}
              disabled={loading}
              size="small"
            />
          </Grid>
        </Grid>

        {/* Security Notice */}
        <Alert severity="info" sx={{ mb: 2 }}>
          🔒 <strong>Secure Payment:</strong> You will be redirected to CCAvenue's secure payment gateway. 
          Your payment information is encrypted and processed securely.
        </Alert>
      </CardContent>
      
      <Divider />
      
      <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={() => {
            handleCancelPayment();
            if (onClose) onClose();
          }} 
          disabled={loading}
          color="secondary"
          startIcon={<Typography>←</Typography>}
        >
          Back to Payment Methods
        </Button>
        <Button
          onClick={handleSubmitPayment}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          size="large"
        >
          {loading ? 'Processing...' : `Pay ₹${amount || flat.outstandingBalance}`}
        </Button>
      </CardActions>
      
      <form ref={formRef} style={{ display: 'none' }} />
      
      {/* Terms and Conditions Dialog */}
      <Dialog 
        open={termsDialogOpen} 
        onClose={() => setTermsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Terms and Conditions for Payment Processing
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
            General Payment Gateway Terms and Conditions
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            By using our payment gateway services, you agree to be bound by these terms and conditions. 
            If you do not agree with any part of these terms, please do not proceed with the payment.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            2. Payment Processing and Security
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • All payments are processed through CCAvenue, a PCI DSS compliant payment gateway
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Your payment information is encrypted using 256-bit SSL encryption
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • We do not store your card details, banking information, or payment credentials
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            3. Supported Payment Methods
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Credit Cards (Visa, MasterCard, American Express, Diners Club)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Debit Cards (Visa, MasterCard, RuPay)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Net Banking from major banks
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • UPI (Unified Payments Interface)
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • Digital Wallets and Buy Now Pay Later options
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            4. Transaction Charges and Fees
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Transaction charges may apply based on your chosen payment method
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Charges are as per the payment gateway's current rate card
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • Any applicable charges will be displayed before payment confirmation
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            5. Payment Authorization and Processing
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • You authorize us to charge the specified amount to your chosen payment method
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Payments are processed in real-time and confirmation is sent immediately
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • You must ensure sufficient funds/credit limit before initiating payment
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            6. Failed Transactions and Refunds
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Failed transactions will be automatically refunded within 5-7 business days
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Refund processing time may vary based on your bank/card issuer
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • Contact customer support if refund is not received within the specified time
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            7. Data Privacy and Protection
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • We comply with applicable data protection laws and regulations
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Transaction data is used only for payment processing and account management
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • We do not share your payment information with unauthorized third parties
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            8. Dispute Resolution and Customer Support
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • For payment-related issues, contact building administration first
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • For technical payment gateway issues, contact CCAvenue customer support
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • Keep transaction receipts and reference numbers for future queries
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            9. Liability and Disclaimers
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • We are not liable for issues arising from bank/network connectivity problems
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
            • Payment gateway downtimes or maintenance are beyond our control
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            • Users are responsible for ensuring accuracy of payment details entered
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            10. Modification of Terms
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, pl: 2 }}>
            These terms may be updated periodically. Continued use of the payment service 
            constitutes acceptance of any modifications to these terms and conditions.
          </Typography>
          
          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Important Notice:
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              By proceeding with the payment, you acknowledge that you have read, understood, 
              and agree to be bound by these General Payment Gateway Terms and Conditions.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setTermsDialogOpen(false)}
            color="secondary"
            variant="outlined"
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              setTermsAccepted(true);
              setTermsDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Accept Terms
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CCAvPaymentCard;
